// components/Login.tsx - ログイン画面コンポーネント

import React, { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Link,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import PasswordTextField from './PasswordTextField';
import {
  hasFullAuthenticatedSession,
  useAuth,
  type User,
} from '../contexts/AuthContext';
import {
  getApiErrorStatus,
  getUserFacingApiErrorMessage,
} from '../utils/apiErrorMessage';
import { normalizeEmail } from '../utils/normalizeEmail';

type LoginRedirectUser = Pick<
  User,
  | 'isAdmin'
  | 'isVip'
  | 'isTrial'
  | 'hasActiveSubscription'
  | 'isExpired'
  | 'mustChangePassword'
  | 'needsProfileCompletion'
>;

const getSafeInternalPath = (path: unknown): string =>
  typeof path === 'string' && path.startsWith('/') && !path.startsWith('//')
    ? path
    : '/';

const getAuthenticatedDestination = (
  user: LoginRedirectUser | null | undefined,
  requestedPath: unknown,
): string => {
  if (!user) {
    return '/';
  }
  if (user.mustChangePassword) {
    return '/first-login-password';
  }
  if (user.hasActiveSubscription && user.needsProfileCompletion) {
    return '/trial-profile-setup';
  }

  let target = getSafeInternalPath(requestedPath);
  if (
    user.isAdmin ||
    user.isVip ||
    user.isTrial ||
    user.hasActiveSubscription ||
    user.isExpired
  ) {
    if (target === '/login' || target === '/subscription' || target === '/register') {
      target = '/';
    }
    return target;
  }

  return '/subscription';
};

const getLoginErrorMessage = (error: any): string => {
  const detail = error?.response?.data?.detail;
  const errorCode = error?.response?.data?.error;
  const status = getApiErrorStatus(error);

  if (status === 403 && detail === 'viewer_activation_required') {
    return '本人確認が完了していません。認証メールをご確認ください。';
  }
  if (status === 403 && detail === 'account_disabled') {
    return 'このアカウントは現在利用できません。管理者にお問い合わせください。';
  }
  if (errorCode === 'subscription_required') {
    return 'アクティブなサブスクリプションが必要です。';
  }
  if (status === 401) {
    return 'ログインに失敗しました。メールアドレスまたはパスワードが正しくありません。';
  }
  if (status === 504) {
    return 'サーバーからの応答がタイムアウトしました。時間をおいて再試行してください。';
  }

  return getUserFacingApiErrorMessage(error);
};

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [subscriptionSuccess, setSubscriptionSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  const {
    login,
    loading: authLoading,
    isAuthenticated,
    user: authenticatedUser,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const passwordChangedReauth = searchParams.get('reauth') === 'passwordChanged';
  const requiresReauthentication =
    passwordChangedReauth ||
    searchParams.get('reauth') === 'verificationChanged' ||
    searchParams.has('sessionExpired');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setSessionExpired(params.has('sessionExpired'));
    const successParam = params.get('subscriptionSuccess');
    setSubscriptionSuccess(successParam === '1' || successParam === 'true');
  }, [location.search]);

  const requestedPath = location.state?.from?.pathname;

  useEffect(() => {
    if (
      authLoading !== false ||
      !isAuthenticated ||
      !hasFullAuthenticatedSession(authenticatedUser) ||
      requiresReauthentication
    ) {
      return;
    }

    navigate(
      getAuthenticatedDestination(authenticatedUser, requestedPath),
      { replace: true },
    );
  }, [
    authLoading,
    authenticatedUser,
    isAuthenticated,
    navigate,
    requestedPath,
    requiresReauthentication,
  ]);

  // リダイレクト元のパスを取得（ない場合はホームへ）
  const from = passwordChangedReauth ? '/' : getSafeInternalPath(requestedPath);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const normalizedEmail = normalizeEmail(email);

    try {
      const user = await login(normalizedEmail, password);

      // justRegisteredフラグはサーバで新規登録直後ユーザーにはtrueにすること
      if (user.justRegistered) {
        // 新規登録直後は認証コード画面へ
        navigate('/verify', { state: { email: normalizedEmail, registration: true, mode: 'registration', verificationChallengeId: user.verificationChallengeId } });
        return;
      }

      // The server-bound challenge cookie is authoritative. Local storage can
      // never bypass a server-required verification step.
      if (user.mustVerifyCode) {
        navigate('/verify', { state: { email: normalizedEmail, mode: 'login', verificationChallengeId: user.verificationChallengeId } });
        return;
      }

      if (user.mustChangePassword) {
        navigate('/first-login-password', { replace: true });
        return;
      }

      navigate(getAuthenticatedDestination(user, from), { replace: true });
    } catch (err: any) {
      setError(getLoginErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  // パスワード表示切り替え
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" component="h1" align="center" gutterBottom>
          手指衛生データ分析アプリログイン
        </Typography>

        {subscriptionSuccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            サブスクリプションの購入が完了しました。ログインしてアプリをご利用ください。
          </Alert>
        )}

        {sessionExpired && (
          <Alert severity="info" sx={{ mb: 3 }}>
            一定時間操作がなかったため自動的にログアウトしました。
	    <br />
	    再度ログインしてください。
          </Alert>
        )}

        {passwordChangedReauth && (
          <Alert severity="info" sx={{ mb: 3 }}>
            パスワードの変更が完了しました。新しいパスワードで再度ログインしてください。
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="メールアドレス"
            type="email"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
          />
          <PasswordTextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="パスワード"
            type={showPassword ? "text" : "password"}
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isLoading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="パスワードの表示切り替え"
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2, py: 1.5 }}
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={24} sx={{ color: 'white' }} />
            ) : (
              'ログイン'
            )}
          </Button>

          <Box sx={{ mt: 1, textAlign: 'center' }}>
            <Link component={RouterLink} to="/request-password-reset" variant="body2">
              パスワードをお忘れですか？
            </Link>
          </Box>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              アカウントをお持ちでない場合は、
              <Link component={RouterLink} to="/register">
                新規登録
              </Link>
              してください。
            </Typography>
          </Box>

          <Box sx={{ mt: 1, textAlign: 'center' }}>
            <Link component={RouterLink} to="/trial-request" variant="body2">
              お試しプランはこちら
            </Link>
          </Box>
        </Box>
      </Paper>

    </Container>
  );
};

export default Login;
